import MojitoCore from 'mojito/core';
// import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import MojitoApplications from 'mojito/applications';
import MojitoPresentation from 'mojito/presentation';
import {settingsStorage} from 'mojito/core/services/system-settings/slice';

import {isDinosaur} from '#core/utils/dinosaur-detector.js';
import {currentScriptOrigin, ensureStartingSlash} from '#core/utils/url-utils.js';
import {Logger} from '#core/utils/logger.js';
import {mergeTo, pickProperties} from '#core/utils/config-utils.js';
import {generateUniqueId} from '#core/utils/utils.js';
import {getExternalUrlIntent} from '#core/utils/intent-factory.js';
import {DATA_TYPE} from '#core/utils/data-validation.cjs';
import {SpriteMapStorage} from '#core/utils/spritemap-storage/index.js';
import {resolveConfigByCurrentContext} from '#core/utils/context-utils.js';
import {printAutomationStatistics} from '#core/utils/statistics.js';
import {
    checkFontFaceAvailable,
    getCSSFromFontsArray,
    getResettingCSSForContainerId,
} from '#core/utils/embedding-utils.js';

import customViewsConfig from '#core/config/custom-views-config.js';
import COMMON_FONTS from '#core/config/fonts.yaml';
import {Localization} from '#core/localization.js';
import {DebugFlags} from '#core/application/debug/debugFlags.js';
import {API_MESSAGES, INITIALIZATION_STATE} from '#core/application/api/types.js';
import {getBalances, updateBalances} from '#core/application/api/balances-proxy/index.js';
import {WidgetsProviderService} from '#core/application/widgets-provider/index.js';
import {ApplicationConfig} from '#core/config/application-config.js';
import {allFeatures, registerTypeTransformHandler} from '#core/application/abstract-feature.js';
import {DBXPerformanceService, METRIC} from '#core/services/performance-reporting-service/index.js';
import {cancelAppLoadedNotifier, createAppLoadedNotifier} from '#core/application/application-loaded-notifier.js';
import {bootstrapVisionTransport} from '#core/application/application.vision.js';

import {BasePalette} from '#core/application/modules/base-palette.js';

import {getCommonMojitoConfig} from './application.common-moj-config.js';

const reduxInstance = MojitoCore.Services.redux;
const {dispatch} = MojitoCore.Services.redux.store;
const {actions: SystemSettingsActions} = MojitoCore.Services.SystemSettings;
const AuthenticationActions = MojitoServices.Authentication.actions;
const AuthenticationTypes = MojitoServices.Authentication.types;
const SSOAuthenticationService = MojitoServices.Authentication.SSOAuthenticationService;
const {prefetchDataSuccess} = MojitoServices.Prefetch.actions;
const UserSettingsActions = MojitoServices.UserSettings.actions;
const UserInfoActions = MojitoServices.UserInfo.actions;
const {selectors: routerSelectors, actions: routerActions} = MojitoApplications.Common.RouterStore;
const IntentActions = MojitoCore.Intents.actions;
const CookieConsentActions = MojitoCore.Services.CookieConsent.actions;

const Config = MojitoCore.Services.Config;

function printVersion(log) {
    const STYLE_HEAD = 'color: dodgerblue; font-weight: bold';
    let buildDate = new Date();

    buildDate.setTime(BUILD_TIMESTAMP);
    buildDate = buildDate.toLocaleString('en-GB', {hour12: false});

    log.info(
        `%cVer. ${APP_VERSION}, sha: ${GIT_HASH}` + (process.env.NODE_ENV === 'development' ? ' / DEBUG mode' : ''),
        STYLE_HEAD
    );
    log.info(`%cBuild date: ${buildDate} EEST`, STYLE_HEAD);
}

export {getCommonMojitoConfig} from './application.common-moj-config.js';

function featureAllowed(feature) {
    if (DebugFlags.no3rdParties || WidgetsProviderService.isFullscreenWidget()) {
        if (feature.isThirdParty) return false;
    }

    return true;
}

function restoreSessionIfLost({payload: reason}) {
    if (reason === AuthenticationTypes.LOGOUT_REASON.UNEXPECTED_SESSION_LOST) {
        dispatch(AuthenticationActions.restoreSession());
    }
}

function setDefaultConfigValues(configs = {}) {
    for (const configId in configs) {
        const configValue = configs[configId];

        Config.configRegistry.add({
            ...configValue,
            name: configId,
        });
    }
}

function printDebugInfo(log, mojitoConfig) {
    log.group('%cDebug info', Logger.STYLE_DEBUG);
    window.DBX.statistics = printAutomationStatistics(mojitoConfig.services.configurations);
    log.groupEnd();
}

const supplementaryContainerId = `${PRODUCT_NAME}-supplementary`;

function mountSupplementary(configuration, log) {
    function mount() {
        try {
            if (document.getElementById(supplementaryContainerId)) {
                return;
            }

            const fonts = COMMON_FONTS.concat(configuration?.fonts || []);
            let notLoadedFonts = [];

            for (let i = 0; i < fonts.length; i++) {
                if (!checkFontFaceAvailable(fonts[i].family)) {
                    notLoadedFonts.push(fonts[i]);
                }
            }

            const supplementalEl = document.createElement('DIV');
            supplementalEl.id = supplementaryContainerId;

            SpriteMapStorage.init(
                supplementalEl,
                configuration?.customSportIconsMapping || {},
                ApplicationConfig.initParams.postInitConfig
            );

            // The skeleton SVG includes animations. These animations indirectly affect all other SVGs
            // in the spritemap, causing the browser to recalculate styles every time any sprite is rendered on screen.
            // So we move out skeleton SVG to a separate element to avoid this issue.
            const skeleton = document.createElement('DIV');
            skeleton.id = `${supplementalEl.id}-skeleton`;
            skeleton.style.setProperty('display', 'none'); // this actually disable animation recalc
            skeleton.style.setProperty('width', '0');
            skeleton.style.setProperty('height', '0');
            skeleton.style.setProperty('overflow', 'hidden');
            skeleton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg"><symbol id="svgsprite-skeleton-part" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice"><style>rect.skeleton-part-rect {animation-duration: 1.5s;animation-iteration-count: infinite;animation-direction: alternate;animation-name: skeleton-part-animation;will-change: opacity} @keyframes skeleton-part-animation { from {opacity: 0.4;} to {opacity: 1;}}</style><rect width="100%" height="100%" class="skeleton-part-rect"></rect></symbol></svg>`;
            supplementalEl.appendChild(skeleton);
            //----

            if (notLoadedFonts.length > 0) {
                const style = document.createElement('style');
                style.id = `${supplementalEl.id}-font-styles`;
                style.innerHTML = getCSSFromFontsArray(notLoadedFonts);
                supplementalEl.appendChild(style);
            }

            document.body.appendChild(supplementalEl);
        } catch (e) {
            log.error('Failed to mount supplementary', e);
        }
    }

    if (document.readyState === 'loading') {
        // If the document is still loading, mount after DOMContentLoaded
        document.addEventListener('DOMContentLoaded', mount);
    } else {
        // If the document is already loaded, mount immediately
        mount();
    }
}

export function unmountSupplementary(log) {
    const el = document.getElementById(supplementaryContainerId);
    if (!el) {
        log.error('Cannot unmount supplementary');

        return;
    }
    el.parentNode.removeChild(el);

    SpriteMapStorage.dispose();
}

function forwardMetrics() {
    performance.measure(`dbx:${PRODUCT_NAME}:init`, 'dbx:init:start', 'dbx:init:end');
    DBXPerformanceService.reportSystemMeasurementStartTime('dbx:init:start', METRIC.INIT_START);
    DBXPerformanceService.reportSystemMeasurementDuration(`dbx:${PRODUCT_NAME}:init`, METRIC.INIT_DURATION);
}

const {INIT_NONE, INIT_INITIALIZING, INIT_DONE} = INITIALIZATION_STATE;

function pickInitParams(params) {
    const urlQueryParams = new URLSearchParams(window.location.search);
    const result = pickProperties(params, [
        'APIVersion',
        'containerId',
        'routingPrefix',
        'language',
        'applicationPlaneZIndex',
        'overlayPlaneZIndex',
        'overlayContainerId',
        'cookieConsentGiven',
        'currencyCode',
        'uiContext',
        'authMethod',
        'configNameToLoad',
        'postInitConfig',
        'standalone',
        'hideBottomNavBar',
        'hidePageBackButton',
        'betslipButtonBottomOffset',
    ]);

    if (!IS_LOTTO) {
        if (params.sportsbookPlaneZIndex) {
            params.applicationPlaneZIndex = params.sportsbookPlaneZIndex;
        }
    }

    switch (result.APIVersion) {
        case 5:
        case 4:
        case 3:
            break;
        case 2:
        case 1:
            throw Error('API version < 3 is not supported anymore');
        default:
            throw Error(
                'APIVersion is not specified or invalid. Probably you are calling .init without APIVersion parameter specified'
            );
    }

    if (result.routingPrefix) {
        result.routingPrefix = ensureStartingSlash(result.routingPrefix);
    }

    result.hideBottomNavBar = result.hideBottomNavBar ?? urlQueryParams.has('hideBottomNavBar');
    result.hidePageBackButton = result.hidePageBackButton ?? urlQueryParams.has('hidePageBackButton');
    result.betslipButtonBottomOffset = parseInt(
        result.betslipButtonBottomOffset ?? urlQueryParams.get('betslipButtonBottomOffset')
    );

    if (!Number.isFinite(result.betslipButtonBottomOffset)) delete result.betslipButtonBottomOffset;

    return result;
}

export function init({log, state, _initParams, mojitoBootstrap, enrichMojitoConfigWithCustomFn, getRuntimeConfigFn}) {
    if (isDinosaur) {
        return Promise.reject('DINOSAUR');
    }

    if (state.initializationState !== INIT_NONE) {
        log.error(`${PRODUCT_NAME} is already initialized. Ignoring "init" call...`);

        return state.initializationPromise;
    }

    performance.mark('dbx:init:start');

    try {
        printVersion(log);

        state.initializationState = INIT_INITIALIZING;
        state.uid = generateUniqueId(); // will be required and referenced later
        state.overlayId = `overlays-${state.uid}`;
        try {
            ApplicationConfig.updateInitParams(pickInitParams(_initParams));
            ApplicationConfig.rebuildUrls();
        } catch (e) {
            return Promise.reject(e.message);
        }

        registerTypeTransformHandler(DATA_TYPE.INTENT, getExternalUrlIntent);
        const initParams = ApplicationConfig.initParams;
        const bundledAppConfig = ApplicationConfig.bundledConfig;
        const externalAppConfig = _initParams.configuration || {};

        const additionalContext = initParams.uiContext || settingsStorage.getAdditionalContext() || '';
        dispatch(SystemSettingsActions.updateAdditionalContext({additionalContext})); // Configure it prior mojito

        // --- Init perf service
        DBXPerformanceService.initOnce();

        return new Promise((success, fail) => {
            log.info('%c[1/7] Loading external config and translations...', Logger.STYLE_PROGRESS);

            const translationsPromise = Localization.init(initParams.language);
            const configPromise = ApplicationConfig.init(bundledAppConfig, externalAppConfig, {
                configNameToLoad: initParams.configNameToLoad,
                runtimeConfigFn: getRuntimeConfigFn,
            });

            Promise.all([configPromise, translationsPromise])
                .then(() => {
                    performance.mark('dbx:config-loaded');

                    if (ApplicationConfig.configAccessViolated) throw new Error('Access violation');

                    Localization.configure(ApplicationConfig.config.translations);

                    // --- print some info
                    ApplicationConfig.apiUrl !== currentScriptOrigin &&
                        log.debug(`%cAPI url is set to ${ApplicationConfig.apiUrl}`, Logger.STYLE_DEBUG);
                    ApplicationConfig.assetsLocation !== currentScriptOrigin + '/' &&
                        log.info(`%cAssets location is set to ${ApplicationConfig.assetsLocation}`, Logger.STYLE_DEBUG);
                    ApplicationConfig.websocketUrl &&
                        log.info(
                            `%cWebsocket location is set to ${ApplicationConfig.websocketUrl}`,
                            Logger.STYLE_DEBUG
                        );

                    log.info(
                        `%cCurrent language is ${Localization.getCurrentDictionaryLang()} / ${Localization.getCurrentLanguage()}`,
                        Logger.STYLE_DEBUG
                    );
                    // ---
                })
                .then(() => {
                    if (IS_VISION) {
                        return bootstrapVisionTransport({
                            contentUrl: ApplicationConfig.contentUrl,
                            broadcastUrl: ApplicationConfig.apiUrl,
                            websocketUrl: ApplicationConfig.websocketUrl,
                            wasmFileUrl: ApplicationConfig.wasmFileUrl,
                        });
                    }
                })
                .then(() => {
                    reduxInstance.actionListener.startListening({
                        actionCreator: AuthenticationActions.disposeSession,
                        effect: restoreSessionIfLost,
                    });

                    if (DebugFlags.analyzeConfigs) ApplicationConfig.analyzeConfigs();

                    allFeatures.setupConfigs(
                        resolveConfigByCurrentContext(ApplicationConfig.config),
                        initParams,
                        featureAllowed
                    );

                    allFeatures.cookieConsentRestrictionsInit();

                    mountSupplementary(ApplicationConfig.config, log);

                    log.info('%c[2/7] Loading external dependencies...', Logger.STYLE_PROGRESS);

                    allFeatures
                        .beforeMojito()
                        .then(() => {
                            log.info('%c[3/7] Preparing tokens...', Logger.STYLE_PROGRESS);
                            BasePalette.load(ApplicationConfig.config);

                            log.info('%c[4/7] Building mojito configs...', Logger.STYLE_PROGRESS);
                            let mojitoConfig = {
                                core: {},
                                services: {
                                    configurations: {},
                                    stores: {},
                                },
                            };

                            allFeatures.updateViewsConfig(mojitoConfig.services.configurations);
                            mergeTo(
                                mojitoConfig,

                                getCommonMojitoConfig({
                                    noAuthentication: WidgetsProviderService.getWidgetData().noAuthenticationService,
                                    overlayRandomId: state.overlayId,
                                })
                            );
                            allFeatures.updateMojitoConfig(mojitoConfig);

                            if (enrichMojitoConfigWithCustomFn)
                                enrichMojitoConfigWithCustomFn(mojitoConfig, ApplicationConfig.config);

                            mergeTo(mojitoConfig, ApplicationConfig.mojitoConfiguration);

                            const mojDefConfigsStart = performance.now();
                            setDefaultConfigValues(customViewsConfig);
                            performance.measure('dbx:mojito-set-default-configs', {
                                start: mojDefConfigsStart,
                            });

                            window.DBX = {
                                ...window.DBX,
                                mojitoConfig,
                                spriteMapStorage: SpriteMapStorage,
                                appConfig: ApplicationConfig.config,
                                basePalette: BasePalette.palette,
                                allFeatures: allFeatures,
                                publishIntent(intentType, intentData) {
                                    dispatch(IntentActions.publishIntent(intentType, intentData));
                                },
                                parseAndPublishIntent(intentStr) {
                                    const intent = getExternalUrlIntent(intentStr);
                                    this.publishIntent(intent.type, intent.data);
                                },
                            };
                            if (process.env.NODE_ENV === 'development') {
                                printDebugInfo(log, mojitoConfig);
                            }

                            // Make Lighthouse happy again :)
                            setTimeout(() => {
                                try {
                                    const mojitoStart = performance.now();
                                    log.info('%c[5/7] Starting up mojito...', Logger.STYLE_PROGRESS);

                                    const promisedInit = mojitoBootstrap.init(mojitoConfig) || Promise.resolve();

                                    promisedInit.then(() => {
                                        performance.measure('dbx:init-mojito', {start: mojitoStart});
                                        log.info('%c[6/7] After mojito...', Logger.STYLE_PROGRESS);
                                        allFeatures
                                            .afterMojito()
                                            .then(() => {
                                                log.info('%c[7/7] Done.', Logger.STYLE_PROGRESS);
                                            })
                                            .then(() => {
                                                state.initializationState = INIT_DONE;
                                                performance.mark('dbx:init:end');
                                                forwardMetrics();

                                                setTimeout(success);
                                            });
                                    });
                                } catch (err) {
                                    log.error('Failed to init Mojito.', err);
                                    fail(err);
                                }
                            });
                        })
                        .catch(error => {
                            log.error('Failure (2).', error);
                            fail(error);
                        });
                })
                .catch(error => {
                    log.error('Failure (1).', error);
                    fail(error);
                });
        });
    } catch (e) {
        log.error(`Initialization failed.`, e);
        performance.mark('dbx:init:end');
        forwardMetrics();
        state.initializationState = INIT_NONE;

        return Promise.reject(e);
    }
}

export function terminate({state, log, mojitoBootstrap}) {
    try {
        if (state.containerNode) {
            unmount({log, state, mojitoBootstrap});
        }
        WidgetsProviderService.unmountAllWidgets();
        unmountSupplementary(log);

        reduxInstance.actionListener.stopListening({
            actionCreator: AuthenticationActions.disposeSession,
            effect: restoreSessionIfLost,
        });

        allFeatures.dispose();
        mojitoBootstrap.terminate();
        state.initializationState = INIT_NONE;
        state.initializationPromise = null;

        return true;
    } catch (e) {
        state.initializationState = INIT_NONE;
        state.initializationPromise = null;
        log.error(e);

        return false;
    }
}

export function mount(containerId, params = {}, {log, state, mojitoBootstrap}) {
    let initParams = ApplicationConfig.initParams;
    if (containerId === 'preloaded-sportsbook') return Promise.resolve(); // Ignore this container. Temporary, until portal fix this

    if (state.initializationState !== INIT_DONE) {
        const msg = `Cannot mount ${PRODUCT_NAME} because it is not initialized. Please call .init() first`;
        log.error(msg);

        return Promise.reject(new Error(msg));
    }

    function mount() {
        if (!containerId) {
            containerId = initParams.containerId;
            if (!containerId) {
                const msg = 'Cannot mount because container ID is not specified';
                log.error(msg);

                return Promise.reject(new Error(msg));
            }
        }
        if (state.containerNode && state.containerNode.id === containerId) {
            // Already mounted at the same node. Ignoring
            return Promise.resolve();
        }

        const containerEl = document.getElementById(containerId);
        if (!containerEl) {
            const msg = `Cannot mount ${PRODUCT_NAME}. No DOM node found with provided id: ${containerId}`;
            log.error(msg);

            return Promise.reject(new Error(msg));
        }

        if (window.$MOJITO_PREFETCH) {
            dispatch(prefetchDataSuccess(window.$MOJITO_PREFETCH));
            delete window.$MOJITO_PREFETCH;
        }
        try {
            // ---- Update initParams
            params = pickProperties(params, [
                'routingPrefix',
                'applicationPlaneZIndex',
                'overlayPlaneZIndex',
                'overlayContainerId',
            ]);
            if (params.routingPrefix) {
                params.routingPrefix = ensureStartingSlash(params.routingPrefix);
            }
            params.containerId = containerId;
            initParams = ApplicationConfig.updateInitParams(params);
            // ----

            if (params.routingPrefix) {
                const routeResolver = routerSelectors.getRouteResolver();
                if (routeResolver.getRoot() !== initParams.routingPrefix) {
                    routeResolver.setRoot(initParams.routingPrefix);
                }
            }

            if (state.containerNode) {
                // Already mounted at different container
                // Need to transfer from old node to new containerId

                // Find all swiper elements and save their scroll options
                const swipers = document.getElementsByClassName('ta-Swiper');
                const scrollOptions = [...swipers].map(({scrollLeft, scrollRight}) => {
                    return {scrollLeft, scrollRight};
                });

                state.containerNode.removeChild(state.reactAppNode);
                state.stylesNode && state.containerNode.removeChild(state.stylesNode);
                state.overlayContainerNode.removeChild(state.overlayNode);

                containerEl.innerHTML = ''; // Clear all contents
                state.stylesNode && containerEl.appendChild(state.stylesNode);
                state.overlayContainerNode =
                    (initParams.overlayContainerId && document.getElementById(initParams.overlayContainerId)) ||
                    containerEl;
                state.overlayContainerNode.appendChild(state.overlayNode);
                containerEl.appendChild(state.reactAppNode);

                setTimeout(function () {
                    // Find all swiper elements and restore their scroll options
                    const newSwipers = document.getElementsByClassName('ta-Swiper');
                    [...newSwipers].forEach((swiper, index) => {
                        swiper.scrollLeft = scrollOptions[index].scrollLeft;
                        swiper.scrollRight = scrollOptions[index].scrollRight;
                    });
                }, 1000);

                state.containerNode = containerEl;

                return Promise.resolve();
            }

            const uid = state.uid;
            const reactAppId = `${PRODUCT_NAME}-${uid}`;
            allFeatures.beforeMount(containerEl);

            containerEl.innerHTML = ''; // Clear all contents

            if (ApplicationConfig.isEmbedded) {
                const style = (state.stylesNode = document.createElement('style'));
                style.id = `reset-styles-${uid}`;
                style.innerHTML = getResettingCSSForContainerId(reactAppId);
                containerEl.appendChild(style);
            } else {
                state.stylesNode = null;
            }

            let reactAppNode = (state.reactAppNode = document.createElement('div'));
            reactAppNode.id = reactAppId;
            if (ApplicationConfig.isEmbedded) {
                reactAppNode.style.position = 'relative';
                reactAppNode.style.zIndex = initParams.applicationPlaneZIndex;
            }
            containerEl.appendChild(reactAppNode);

            state.overlayContainerNode =
                (initParams.overlayContainerId && document.getElementById(initParams.overlayContainerId)) ||
                containerEl;

            let overlay = (state.overlayNode = document.createElement('div'));
            overlay.id = state.overlayId;
            overlay.classList.add('notranslate'); //DBX-10930: Disabled Google Translate on overlays (including mobile betslip)
            state.overlayContainerNode.appendChild(overlay);

            // ----  Adjust DOM
            const start = performance.now();
            DBXPerformanceService.reportTiming(METRIC.RENDER_START, start);
            mojitoBootstrap.render(reactAppNode.id);
            state.containerNode = containerEl;
            performance.measure('dbx:mojito-render', {start});
            allFeatures.afterMount(containerEl);

            return createAppLoadedNotifier();
        } catch (e) {
            log.error(e);

            return Promise.reject(e);
        }
    }

    if (document.readyState === 'loading') {
        // If the document is still loading, call `mount` after DOMContentLoaded
        const DOMContentLoadedPromise = new Promise(resolve => {
            document.addEventListener('DOMContentLoaded', resolve, {once: true});
        });

        return DOMContentLoadedPromise.then(mount);
    } else {
        // If the document is already loaded, call `mount` immediately
        return mount();
    }
}

export function unmount({log, state, mojitoBootstrap}) {
    try {
        if (!state.containerNode) {
            log.warn(`Trying to unmount '${PRODUCT_NAME}' that was not mounted. Ignored`);

            return false;
        }
        cancelAppLoadedNotifier();

        allFeatures.beforeUnmount();
        mojitoBootstrap.unmount();
        state.containerNode.innerHTML = '';
        state.containerNode = null;
        state.reactAppNode = null;
        state.overlayContainerNode = null;
        state.overlayNode = null;
        state.stylesNode = null;
        dispatch(routerActions.reset()); // TODO temporary workaround for old Portal on HollandCasino, see details at DBX-12447

        return true;
    } catch (e) {
        log.error(e);

        return false;
    }
}

export function syncSession(data) {
    if (data) {
        // We cannot use dispatch(AuthenticationActions.login(data)) here because it accepts different data structure
        // Instead we use SSOAuthenticationService.login which use DrivenSSOService internally to get credentials
        return new Promise((resolve, reject) => {
            SSOAuthenticationService.login(
                data,
                () => resolve(),
                error => reject(error)
            );
        });
    } else if (data === null) {
        dispatch(AuthenticationActions.logout());
    } else {
        // data is not set (undefined)
        dispatch(AuthenticationActions.restoreSession());
    }

    return Promise.resolve();
}

/**
 * Handles messages from the host application. Should be used by the host to communicate with embedded widgets.
 * @param log {object} Logger instance
 * @param id {string} Message ID
 * @param data {object}
 * @returns {Promise<void>}
 */
export function sendMessage(log, id, data) {
    try {
        switch (id) {
            case API_MESSAGES.SYNC_SESSION:
                return syncSession(data);
            case API_MESSAGES.CHANGE_TIMEZONE:
                dispatch(UserSettingsActions.updateTimeOffset(data));

                return Promise.resolve();
            case API_MESSAGES.GET_BALANCE: {
                const result = getBalances();

                return Promise.resolve(result);
            }
            case API_MESSAGES.SET_BALANCE: {
                updateBalances(data);

                return Promise.resolve();
            }
            case API_MESSAGES.PERFORMANCE_METRIC: {
                let metrics = Array.isArray(data) ? data : [data];
                for (let metric of metrics) DBXPerformanceService.reportMetric(metric.name, metric.value, metric.unit);

                return Promise.resolve();
            }
            case API_MESSAGES.PREFETCH_DATA: {
                if (!IS_VISION) dispatch(prefetchDataSuccess(data)); // ignore for vision

                return Promise.resolve();
            }
            case API_MESSAGES.GIVE_COOKIE_CONSENT: {
                if (data.analytics) {
                    dispatch(CookieConsentActions.giveConsent());
                }

                return Promise.resolve();
            }
            case API_MESSAGES.MARKETING_DATA: {
                const {affiliateName} = data;
                if (affiliateName) {
                    log.info(`%caffiliateName received: ${affiliateName}`, Logger.STYLE_DEBUG);
                    dispatch(UserInfoActions.updateUserInfo({affiliate: affiliateName}));
                } else {
                    log.error(`%caffiliateName is missing`, Logger.STYLE_DEBUG);
                }

                return Promise.resolve();
            }
            case API_MESSAGES.SHOW_MOBILE_YOUR_BETS: {
                dispatch(
                    IntentActions.publishIntent(MojitoPresentation.Base.Intent.Types.SHOW_SLIDING_OPEN_BETS, {
                        authRequired: true,
                    })
                );

                return Promise.resolve();
            }
            case API_MESSAGES.SHOW_MOBILE_AZ_MENU: {
                dispatch(IntentActions.publishIntent(MojitoPresentation.Base.Intent.Types.SHOW_SLIDING_AZ_MENU));

                return Promise.resolve();
            }
            case API_MESSAGES.HIDE_SLIDING_OVERLAYS: {
                dispatch(IntentActions.publishIntent(MojitoPresentation.Base.Intent.Types.OVERLAY_INTERACTION));

                return Promise.resolve();
            }

            default:
                log.warn(`Unknown message id: ${id}`);

                return Promise.reject();
        }
    } catch {
        return Promise.reject();
    }
}
